import { useState, useMemo, useRef, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { PeopleTable } from "@/components/shared/DrillDownTable";
import { ArrowLeft, Video, Mail, Paperclip, ArrowDownLeft, ArrowUpRight, Users, Play, Pause, Search, Copy, Send, MessageSquare, Sparkles, FileText, ListChecks, Eye, DollarSign, Building2, Phone, MapPin, ExternalLink, TrendingUp, Star, Calendar, Globe, Loader2, Pencil, ArrowUp, ArrowRight, ArrowDown, Calendar as CalendarIcon, Activity } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import UserAvatar from "@/components/shared/Avatar";
import CompanyLogo from "@/components/shared/CompanyLogo";
import LinkedInIcon from "@/components/shared/LinkedInIcon";
import EditableDetailField from "@/components/shared/EditableDetailField";
import MultiValueDetailField from "@/components/shared/MultiValueDetailField";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { downloadFileFromUrl } from "@/lib/utils/downloadFile";
import { toast } from "sonner";
import { people as allPeople, companies as allCompanies, currentUser, workspaceMembers, workspace } from "@/data/dummy";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useTasks } from "@/contexts/TasksContext";
import { parseISO, isValid } from "date-fns";
import { useGetEngagementLazyQuery, useGetNoteQuery, useUpdateNoteMutation, useGetDealLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useFormatters } from "@/lib/i18n/useFormatters";
import { isLovablePreview } from "@/lib/utils/environment";
import { getDisplayName, getSenderFromMetadata } from "@/lib/utils/display-name";
import { useEntityByName } from "@/lib/hooks/use-entity-by-name";
import MailBodyRenderer from "@/components/shared/MailBodyRenderer";
import EngagementPrivacyGate from "@/components/shared/EngagementPrivacyGate";
import NoteEditor from "@/components/shared/NoteEditor";
import type { PartialBlock } from "@blocknote/core";
import { aiNotesToBlocks, type AiNotesSection } from "@/lib/utils/ai-notes";

// Preview-only: dummy company names, offered as suggestions in preview and
// never in production (see the deal company field below).
const companySuggestions = [...new Set(allCompanies.map(c => c.name))] as string[];

// ---- Shared navigation types ----
export interface NoteItem {
  id: string;
  name: string;
  date: string;
  type: "ai" | "manual";
  content: string;
}

export interface PersonItem {
  id: string;
  name: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
}

export type NavigationItem =
  | { type: "meeting"; data: MeetingItem }
  | { type: "email"; data: EmailItem }
  | { type: "file"; data: FileItem }
  | { type: "person"; data: PersonItem }
  | { type: "company"; data: CompanyItem }
  | { type: "deal"; data: DealItem }
  | { type: "briefing"; data: MeetingItem }
  | { type: "note"; data: NoteItem };

export interface DrillDownHeaderProps {
  onBack: () => void;
  title: string;
  subtitle?: string;
}

export function DrillDownHeader({ onBack, title, subtitle }: DrillDownHeaderProps) {
  return (
    <div className="flex items-center gap-3 mb-5 pr-24">
      <button
        onClick={onBack}
        className="shrink-0 rounded-md hover:bg-muted p-1.5 transition"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold truncate">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
      </div>
    </div>
  );
}

// ClickableCompany / ClickablePerson lived here and resolved names against the
// dummy dataset, so they only ever navigated in preview. They had no call
// sites, so they were removed rather than ported — use useEntityByName if a
// name-only drill-down is needed again.

// ---- Meeting detail (full intelligence view) ----
export interface MeetingItem {
  id: string;
  title: string;
  date: string;
  duration: string;
  attendees: string[];
  // Optional backend/navigation fields
  startTime?: string;
  name?: string;
  time?: string;
}

const meetingTranscript = [
  { speaker: currentUser.name, time: "0:00", text: "Alright, thanks for joining everyone. Let's get started with the agenda." },
  { speaker: "Sarah Chen", time: "0:12", text: `Thanks ${currentUser.name}. I wanted to walk through the migration timeline we discussed last week.` },
  { speaker: currentUser.name, time: "0:28", text: "Great, let's hear it. How are things looking on your end?" },
  { speaker: "Sarah Chen", time: "0:35", text: "We've completed the initial assessment. The data migration should take about two weeks, and then we'll need another week for testing." },
  { speaker: currentUser.name, time: "1:02", text: "That's faster than I expected. Any risks we should be aware of?" },
  { speaker: "Sarah Chen", time: "1:10", text: "The main risk is around the legacy API integrations. We'll need your team's help mapping those to new endpoints." },
  { speaker: currentUser.name, time: "1:30", text: "Absolutely, I'll get our integration team looped in. Let's set up a working session for that." },
  { speaker: "Sarah Chen", time: "1:45", text: "Perfect. I'll send over the technical spec document after this call." },
  { speaker: currentUser.name, time: "2:00", text: "Sounds good. Anything else before we wrap up?" },
  { speaker: "Sarah Chen", time: "2:10", text: "Just one thing — we'll need access to the staging environment by Friday." },
  { speaker: currentUser.name, time: "2:20", text: "I'll get that set up today. Thanks Sarah, great progress." },
];

const meetingSummaryPoints = [
  "Migration timeline estimated at 3 weeks total (2 weeks migration + 1 week UAT)",
  "Legacy API integrations identified as the main risk area",
  "Integration team to be looped in for endpoint mapping",
  "Technical spec document to be shared post-meeting",
  "Staging environment access needed by Friday for POC",
];

const meetingNotes = `## Key Discussion Points

### Migration Timeline
- Data migration phase: ~2 weeks
- Testing & UAT phase: ~1 week
- Total estimated timeline: 3 weeks
- Sarah seems confident about the timeline — good sign

### Risk Areas
- **Legacy API Integrations**: The existing API endpoints need to be mapped to the new REST system. This is the primary risk factor.
- ${currentUser.name}'s integration team will assist with endpoint mapping
- The legacy API concern is real — we hit similar issues with Datadog
- Should probably loop in Alex Kim too since he dealt with this before

### Action Items Discussed
- Working session to be scheduled for API mapping
- Technical spec document to be shared after the call
- Staging environment access to be provisioned by Friday
- Need to check with ${workspaceMembers[2].name.split(" ")[0]} about integration team availability
- Friday deadline for staging is tight, talk to DevOps today

### Next Steps
- Sarah to send technical spec document
- ${currentUser.name} to provision staging environment access
- Follow-up working session scheduled for next week`;

const meetingFollowUpEmail = `Hi Sarah,

Thanks for the great discussion today on the migration timeline. Here's a quick recap of what we covered:

- The migration is estimated at 3 weeks (2 weeks migration + 1 week UAT)
- I'm looping in our integration team to help with the legacy API endpoint mapping
- I'll have staging environment access set up for you by Friday

Looking forward to receiving the technical spec document. Let's plan to schedule the working session for early next week.

Best,
${currentUser.name}`;

const meetingSuggestedTasks = [
  { id: "t1", title: "Set up staging environment access", priority: "High" as const, assignee: currentUser.name },
  { id: "t2", title: "Schedule API mapping working session", priority: "High" as const, assignee: currentUser.name },
  { id: "t3", title: "Review technical spec document", priority: "Medium" as const, assignee: currentUser.name },
  { id: "t4", title: "Prepare legacy API endpoint inventory", priority: "Medium" as const, assignee: currentUser.name },
  { id: "t5", title: "Send calendar invite for follow-up check-in", priority: "Low" as const, assignee: currentUser.name },
];

const priorityOptions = ["High", "Medium", "Low"] as const;

const speakerColors = [
  { bg: "bg-sky-500", bgLight: "bg-sky-200 text-sky-800" },
  { bg: "bg-amber-500", bgLight: "bg-amber-200 text-amber-800" },
  { bg: "bg-emerald-500", bgLight: "bg-emerald-200 text-emerald-800" },
  { bg: "bg-violet-500", bgLight: "bg-violet-200 text-violet-800" },
];

// ---- Email helpers ----
interface ViewerInfo {
  displayName: string;
  email?: string;
  aliases: string[];
}

/**
 * Append the address to a participant name in the email header popover.
 *
 * `directory` is a name -> email map built from the engagement's own
 * participants (real contacts + workspace users) in production, and from dummy
 * people/members in preview. It used to read the dummy dataset unconditionally,
 * which in production either found nothing or — worse, for a name that happened
 * to collide with a dummy person — displayed a fabricated address on a real
 * email. Unknown names render bare, which is honest.
 */
function resolveNameWithEmail(name: string, viewer: ViewerInfo, directory: Map<string, string>): string {
  if (viewer.email && viewer.aliases.some(a => a && a === name)) {
    return `${viewer.displayName} <${viewer.email}>`;
  }
  // The name may already be an address (backend headers often are).
  if (name.includes('@')) return name;
  const email = directory.get(name.trim().toLowerCase());
  return email ? `${name} <${email}>` : name;
}

// ---- Email detail with dummy body ----
export interface EmailItem {
  id: string;
  subject: string;
  date: string;
  from: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  direction: "inbound" | "outbound";
}

const emailBodies: Record<string, string> = {
  "1": `<p>Hi Doug,</p>
<p>Just wanted to follow up on our conversation about the migration timeline. We've completed the initial data audit and everything looks good to proceed on schedule.</p>
<p>A few things to flag:</p>
<ul>
  <li>The API endpoint mapping is about 70% complete</li>
  <li>We'll need access to the staging environment by end of week</li>
  <li>Our QA team has started drafting the test plan</li>
</ul>
<p>Let me know if you have any questions or need any additional documentation from our side.</p>
<p>Best regards,<br/>Sarah</p>`,

  "2": `<p>Hi Sarah,</p>
<p>Thanks for the update on the migration progress. Attached is the updated proposal for Q2 with the revised timeline we discussed.</p>
<p><strong>Key changes:</strong></p>
<ul>
  <li>Extended UAT window from 1 week to 10 days based on your team's feedback</li>
  <li>Added a buffer for the API integration phase</li>
  <li>Updated pricing to reflect the additional support hours</li>
</ul>
<p>Please review and let me know if you'd like to discuss any of the changes before we finalize.</p>
<p>Best,<br/>Doug</p>`,

  "3": `<p>Hi Doug,</p>
<p>Following up on our meeting last week. I had a few questions about the pricing breakdown:</p>
<ol>
  <li>Is the support tier flexible? We may need 24/7 coverage for the first month post-migration.</li>
  <li>Can we get a volume discount if we commit to a 2-year term?</li>
  <li>What's included in the "premium onboarding" package?</li>
</ol>
<p>Looking forward to your thoughts.</p>
<p>Thanks,<br/>Alex</p>`,

  "4": `<p>Hi team,</p>
<p>Great call today! Here's a quick summary of what we discussed:</p>
<ul>
  <li>Migration timeline confirmed for 3 weeks</li>
  <li>Sarah's team will handle data migration, our team handles API mapping</li>
  <li>Staging access to be provisioned by Friday</li>
  <li>Next check-in scheduled for Tuesday</li>
</ul>
<p>Action items are tracked in the shared doc. Let me know if I missed anything.</p>
<p>Cheers,<br/>Doug</p>`,

  "5": `<p>Hi Sarah,</p>
<p>It was great meeting you at the conference last week. As discussed, I'd love to explore how we might work together on the platform migration project.</p>
<p>I've attached a brief overview of our capabilities and a few case studies that might be relevant. Would you be available for a 30-minute call next week to discuss further?</p>
<p>Looking forward to connecting.</p>
<p>Best,<br/>Doug</p>`,
};

export function EmailDrillDown({ email, onBack }: { email: EmailItem; onBack: () => void }) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const workspaceId = useWorkspaceId();
  const { currentUser: backendUser } = useCurrentUser();
  const viewer: ViewerInfo = useMemo(() => {
    const fullName = [backendUser?.givenName, backendUser?.surname].filter(Boolean).join(' ');
    const displayName = backendUser?.preferredName || fullName || backendUser?.email || 'me';
    return {
      displayName,
      email: backendUser?.email ?? undefined,
      aliases: [
        backendUser?.preferredName,
        backendUser?.givenName,
        fullName,
        backendUser?.email,
      ].filter((v): v is string => Boolean(v)),
    };
  }, [backendUser?.preferredName, backendUser?.givenName, backendUser?.surname, backendUser?.email]);
  const [getEngagement, { data: engagementData, loading, error: engagementError }] = useGetEngagementLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Fetch engagement details when component mounts
  useEffect(() => {
    if (email?.id && workspaceId && !isLovablePreview()) {
      console.log('[EmailDrillDown] Fetching engagement:', email.id);
      getEngagement({
        variables: {
          id: email.id,
          workspaceId: String(workspaceId),
        },
      });
    }
  }, [email?.id, workspaceId, getEngagement]);

  const engagement = engagementData?.engagement;
  const useBackendData = !isLovablePreview() && engagement;

  // Name -> email directory for the header popover. Production reads the
  // engagement's own participants (contacts + workspace users), which the
  // getEngagement response already carries — no extra fetch, and no dummy.
  const participantDirectory = useMemo(() => {
    const map = new Map<string, string>();
    const add = (name: string | null | undefined, email: string | null | undefined) => {
      if (name && email) map.set(name.trim().toLowerCase(), email);
    };
    if (isLovablePreview()) {
      allPeople.forEach(p => add(p.name, p.email));
      workspaceMembers.forEach(m => add(m.name, m.email));
      return map;
    }
    engagement?.contacts?.forEach(c => {
      if (!c) return;
      add(getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail), c.primaryEmail);
    });
    engagement?.users?.forEach(u => {
      if (!u) return;
      add(getDisplayName(u.givenName, u.surname, u.email), u.email);
      add(u.preferredName, u.email);
    });
    return map;
  }, [engagement]);

  // Get email body from backend or fallback to dummy data
  const body = useBackendData && engagement.body
    ? engagement.body
    : emailBodies[email.id] || `<p>Hi,</p><p>Thank you for your email regarding "${email.subject}". I'll review this and get back to you shortly.</p><p>Best regards</p>`;

  // Rich HTML variant (preserves original formatting). MailBodyRenderer prefers
  // it when present; preview mode leaves it undefined so the dummy `body` path
  // renders. The backend `body` field is HTML-stripped to plain text, so without
  // this a multi-paragraph email collapses into a run-on line.
  const bodyHtml = useBackendData ? engagement.bodyHtml : undefined;

  // Get source and mimeType from engagement if available
  const source = engagement?.source;
  const mimeType = (engagement?.metadata as Record<string, unknown>)?.mimeType as string || "text/plain";

  // Prefer the actual "From" header from the fetched engagement so the drill-down
  // reliably shows who sent the email, falling back to the value passed in props.
  const senderName = useBackendData && engagement.metadataFrom
    ? getSenderFromMetadata(engagement.metadataFrom, email.from)
    : email.from;
  // Raw header ("Name <email>") is the richest source for the From popover.
  const senderFull = (useBackendData && engagement.metadataFrom)
    || resolveNameWithEmail(email.from, viewer, participantDirectory);

  const formattedDate = (() => {
    if (!email.date) return '';
    try {
      const d = parseISO(email.date);
      if (isValid(d)) return formatDate(d);
    } catch { /* ignore */ }
    return email.date;
  })();

  return (
    <div>
      <DrillDownHeader onBack={onBack} title={email.subject} subtitle={`${formattedDate} · ${senderName}`} />
      <EngagementPrivacyGate engagementType="email" engagement={engagement} loading={loading && !isLovablePreview()} error={engagementError}>
        <div className="space-y-4">
          <div className="rounded-lg bg-muted/40 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {email.direction === "inbound" ? (
                <ArrowDownLeft className="h-3.5 w-3.5 text-accent" />
              ) : (
                <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              <span className="text-xs text-muted-foreground">
                <Trans
                  i18nKey={email.direction === "inbound" ? "detailSheets.receivedFrom" : "detailSheets.sentBy"}
                  values={{ name: senderName }}
                  components={{ sender: <span className="text-foreground font-medium" /> }}
                />
                {" "}
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="text-xs text-accent hover:underline cursor-pointer inline-flex items-center gap-0.5">
                      {t("detailSheets.emailTo", {
                        recipient: (email.to || (email.direction === "inbound" ? [viewer.displayName] : [email.from])).slice(0, 1).join(", "),
                      })}
                      {((email.to?.length || 1) + (email.cc?.length || 0) + (email.bcc?.length || 0)) > 1 && (
                        <span className="text-muted-foreground"> +{(email.to?.length || 1) + (email.cc?.length || 0) + (email.bcc?.length || 0) - 1}</span>
                      )}
                    </button>
                  </PopoverTrigger>
                   <PopoverContent className="w-80 p-3 space-y-2 text-xs" align="start">
                    <div>
                      <span className="text-muted-foreground font-medium uppercase tracking-wider text-[10px]">{t("detailSheets.emailFromLabel")}</span>
                      <p className="text-foreground">{senderFull}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground font-medium uppercase tracking-wider text-[10px]">{t("detailSheets.emailToLabel")}</span>
                      {(email.to || (email.direction === "inbound" ? [viewer.displayName] : [email.from])).map((name, i) => (
                        <p key={i} className="text-foreground">{resolveNameWithEmail(name, viewer, participantDirectory)}</p>
                      ))}
                    </div>
                    {(email.cc && email.cc.length > 0) && (
                      <div>
                        <span className="text-muted-foreground font-medium uppercase tracking-wider text-[10px]">{t("detailSheets.emailCcLabel")}</span>
                        {email.cc.map((name, i) => (
                          <p key={i} className="text-foreground">{resolveNameWithEmail(name, viewer, participantDirectory)}</p>
                        ))}
                      </div>
                    )}
                    {(email.bcc && email.bcc.length > 0) && (
                      <div>
                        <span className="text-muted-foreground font-medium uppercase tracking-wider text-[10px]">{t("detailSheets.emailBccLabel")}</span>
                        {email.bcc.map((name, i) => (
                          <p key={i} className="text-foreground">{resolveNameWithEmail(name, viewer, participantDirectory)}</p>
                        ))}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </span>
            </div>
            <p className="text-sm font-medium">{email.subject}</p>
            <p className="text-xs text-muted-foreground">{formattedDate}</p>
          </div>
          <div className="rounded-lg border border-border p-4 leading-relaxed">
            <MailBodyRenderer body={body} bodyHtml={bodyHtml} source={source} mimeType={mimeType} />
          </div>
          <a
            href={`https://mail.google.com/mail/#search/${encodeURIComponent(email.subject)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1.5 rounded-md bg-foreground text-background hover:opacity-90 flex items-center gap-1.5 transition font-medium inline-flex"
          >
            <ExternalLink className="h-3 w-3" />
            {/* Distinct from `common.open` (the task STATUS "Open") — this is
                the verb, and several languages inflect the two differently. */}
            {t("detailSheets.openAction")}
          </a>
        </div>
      </EngagementPrivacyGate>
    </div>
  );
}

// ---- File detail ----
export interface FileItem {
  id: string;
  name: string;
  size: string;
  date: string;
  source?: string;
  url?: string;
}

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg"]);
const PDF_EXTENSIONS = new Set(["pdf"]);

const filePreviewContent: Record<string, { type: "pdf" | "image" | "spreadsheet" | "document" | "text"; content: string }> = {
  "1": { type: "document", content: "# Q2 Migration Proposal\n\n## Executive Summary\nThis document outlines the proposed migration plan for transitioning from the legacy platform to the new cloud-based infrastructure.\n\n## Timeline\n- **Phase 1**: Data audit and mapping (2 weeks)\n- **Phase 2**: Core migration (3 weeks)\n- **Phase 3**: UAT and validation (1 week)\n- **Phase 4**: Go-live and monitoring (1 week)\n\n## Budget\nTotal estimated cost: $245,000\n- Infrastructure: $120,000\n- Professional services: $85,000\n- Contingency: $40,000\n\n## Risk Assessment\n| Risk | Likelihood | Impact | Mitigation |\n|------|-----------|--------|------------|\n| Data loss | Low | Critical | Incremental backups |\n| Downtime | Medium | High | Blue-green deployment |\n| API breaks | Medium | Medium | Compatibility layer |" },
  "2": { type: "spreadsheet", content: "Pricing Comparison - Enterprise Plans\n\n| Feature          | Basic    | Pro      | Enterprise |\n|-----------------|----------|----------|------------|\n| Users           | Up to 10 | Up to 50 | Unlimited  |\n| Storage         | 100 GB   | 1 TB     | 5 TB       |\n| API Calls/mo    | 10,000   | 100,000  | Unlimited  |\n| Support         | Email    | Priority | Dedicated  |\n| SLA             | 99.5%    | 99.9%    | 99.99%     |\n| Monthly Price   | $299     | $899     | $2,499     |\n| Annual Price    | $2,990   | $8,990   | $24,990    |" },
  "3": { type: "pdf", content: "MASTER SERVICES AGREEMENT\n\nThis Master Services Agreement (\"Agreement\") is entered into as of January 15, 2025.\n\nBETWEEN:\nCoffee CRM, Inc. (\"Provider\")\nAND:\nAcme Corporation (\"Client\")\n\n1. SCOPE OF SERVICES\nProvider shall deliver the software platform and related professional services as described in the applicable Statement of Work.\n\n2. TERM\nThis Agreement shall commence on the Effective Date and continue for a period of twenty-four (24) months.\n\n3. FEES AND PAYMENT\nClient shall pay Provider the fees set forth in each SOW within thirty (30) days of invoice date.\n\n4. CONFIDENTIALITY\nEach party agrees to maintain the confidentiality of all proprietary information disclosed by the other party." },
  "4": { type: "image", content: "architecture-diagram" },
  "5": { type: "text", content: "Meeting Notes - Technical Review\nDate: Jan 10, 2025\nAttendees: Doug, Sarah Chen, Alex Kim\n\n- Reviewed current system architecture\n- Identified 3 critical integration points\n- Database schema changes approved\n- Next review scheduled for Jan 17" },
};

function FilePreview({ file }: { file: FileItem }) {
  const { t } = useTranslation();
  const ext = file.name.split(".").pop()?.toLowerCase() || "";

  // Production mode: render real files by extension + URL
  if (file.url && IMAGE_EXTENSIONS.has(ext)) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 flex items-center justify-center p-4">
        <img
          src={file.url}
          alt={file.name}
          className="max-w-full max-h-[400px] rounded object-contain"
        />
      </div>
    );
  }

  if (file.url && PDF_EXTENSIONS.has(ext)) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/30 rounded-t-lg">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("detailSheets.filePdfDocument")}</span>
        </div>
        <iframe src={file.url} title={file.name} className="w-full h-[500px] rounded-b-lg" />
      </div>
    );
  }

  // Preview mode: render dummy file content by ID
  const preview = filePreviewContent[file.id];
  if (preview) {
    if (preview.type === "image") {
      return (
        <div className="rounded-lg border border-border bg-muted/20 flex items-center justify-center p-6 aspect-video">
          <div className="text-center space-y-2">
            <div className="mx-auto w-48 h-32 rounded bg-gradient-to-br from-accent/20 to-primary/10 border border-border flex items-center justify-center">
              <FileText className="h-10 w-10 text-muted-foreground/40" />
            </div>
            <p className="text-xs text-muted-foreground">{file.name}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/30 rounded-t-lg">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {ext === "xlsx" || ext === "csv"
              ? t("detailSheets.fileSpreadsheet")
              : ext === "pdf"
              ? t("detailSheets.filePdfDocument")
              : ext === "docx"
              ? t("detailSheets.fileDocument")
              : t("detailSheets.fileTextFile")}
          </span>
        </div>
        <div className="p-4 max-h-[400px] overflow-y-auto scrollbar-thin">
          <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-foreground/80">{preview.content}</pre>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/20 flex flex-col items-center justify-center py-16 gap-3">
      <Eye className="h-8 w-8 text-muted-foreground/40" />
      <p className="text-muted-foreground text-xs">{t("detailSheets.filePreviewUnavailable")}</p>
    </div>
  );
}

export function FileDrillDown({ file, onBack }: { file: FileItem; onBack: () => void }) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const formattedDate = (() => {
    if (!file.date) return '';
    try {
      const d = parseISO(file.date);
      if (isValid(d)) return formatDate(d);
    } catch { /* ignore */ }
    return file.date;
  })();

  return (
    <div>
      <DrillDownHeader onBack={onBack} title={file.name} subtitle={`${file.size}${formattedDate ? ` · ${formattedDate}` : ''}`} />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <Paperclip className="h-3.5 w-3.5" />
          <span>{file.size}</span>
          {formattedDate && <><span>·</span><span>{formattedDate}</span></>}
          {file.source && <><span>·</span><span>{file.source}</span></>}
        </div>
        <button
          onClick={() => {
            if (file.url) {
              void downloadFileFromUrl(file.url, file.name);
            } else {
              toast.error(t("detailSheets.fileUrlUnavailable"));
            }
          }}
          className="text-xs px-3 py-1.5 rounded-md border border-border bg-card text-foreground hover:bg-secondary flex items-center gap-1.5 transition font-medium"
        >
          <ArrowDownLeft className="h-3 w-3" />
          {t("detailSheets.download")}
        </button>
      </div>
      <FilePreview file={file} />
    </div>
  );
}

// ---- Company detail (full drill-down with tabs) ----
export interface CompanyItem {
  id: string;
  name: string;
  industry?: string;
  description?: string;
  location?: string;
  website?: string;
  employees?: string;
  revenue?: string;
}

const companyMeetings: MeetingItem[] = [
  { id: "c1", title: "Quarterly Business Review", date: "Feb 27, 2026", duration: "45 min", attendees: ["Sarah Chen", "Doug"] },
  { id: "c2", title: "Product Roadmap Sync", date: "Feb 18, 2026", duration: "30 min", attendees: ["Sarah Chen", "Alex Kim"] },
  { id: "c3", title: "Onboarding Kickoff", date: "Feb 5, 2026", duration: "60 min", attendees: ["Doug", "Lisa Park"] },
];

const companyEmails: EmailItem[] = [
  { id: "1", subject: "Re: Platform Migration Timeline", date: "Feb 28, 2026", from: "Sarah Chen", direction: "inbound" },
  { id: "2", subject: "Proposal Draft v2", date: "Feb 25, 2026", from: "Doug", direction: "outbound" },
  { id: "3", subject: "Re: Pricing Questions", date: "Feb 20, 2026", from: "Alex Kim", direction: "inbound" },
];

const companyDeals: DealItem[] = [
  { id: "1", name: "Platform Migration", stage: "Proposal", amount: 85000 },
  { id: "2", name: "Annual Support Contract", stage: "Negotiation", amount: 42000 },
];

type CompanySubDrill =
  | { type: "meeting"; data: MeetingItem }
  | { type: "email"; data: EmailItem }
  | { type: "person"; data: PersonItem }
  | { type: "deal"; data: DealItem }
  | { type: "briefing"; data: MeetingItem };

// ---- Deal detail (full drill-down with tabs) ----
export interface DealItem {
  id: string;
  name: string;
  stage?: string;
  amount?: number;
  company?: string;
}

const dealMeetings: MeetingItem[] = [
  { id: "d1", title: "Deal Review & Next Steps", date: "Feb 27, 2026", duration: "45 min", attendees: ["Sarah Chen", "Doug"] },
  { id: "d2", title: "Pricing Discussion", date: "Feb 18, 2026", duration: "30 min", attendees: ["Doug"] },
  { id: "d3", title: "Technical Requirements", date: "Feb 10, 2026", duration: "60 min", attendees: ["Alex Kim", "Doug"] },
];

const dealEmails: EmailItem[] = [
  { id: "1", subject: "Re: Updated Proposal", date: "Feb 28, 2026", from: "Sarah Chen", direction: "inbound" },
  { id: "2", subject: "Updated Proposal — Q2", date: "Feb 25, 2026", from: "Doug", direction: "outbound" },
  { id: "3", subject: "Re: Pricing Breakdown", date: "Feb 19, 2026", from: "Alex Kim", direction: "inbound" },
];

const dealFiles: FileItem[] = [
  { id: "1", name: "Proposal_v2_Final.pdf", size: "3.1 MB", date: "Feb 25, 2026" },
  { id: "2", name: "SOW_Draft.docx", size: "1.2 MB", date: "Feb 18, 2026" },
];

type DealSubDrill =
  | { type: "meeting"; data: MeetingItem }
  | { type: "email"; data: EmailItem }
  | { type: "file"; data: FileItem }
  | { type: "person"; data: PersonItem }
  | { type: "briefing"; data: MeetingItem };

export function DealDrillDown({ deal, onBack, onNavigate }: { deal: DealItem; onBack: () => void; onNavigate?: (item: NavigationItem) => void }) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { formatAmount } = useCurrency();
  const [activeTab, setActiveTab] = useState("summary");
  const [subDrill, setSubDrill] = useState<DealSubDrill | null>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [overrides, setOverrides] = useState<Partial<DealItem>>({});
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();

  // The DealItem prop carries a company *name* only. Production needs the real
  // account (for the logo domain and the company drill-down) and the real
  // contacts for the People tab, so fetch the deal — the same getDeal query
  // DealDetailSheet uses, which returns both.
  const [getDealDetail, { data: dealDetailData }] = useGetDealLazyQuery();
  const { resolveCompany } = useEntityByName();
  useEffect(() => {
    if (!preview && deal?.id && workspaceId) {
      getDealDetail({ variables: { id: deal.id } });
    }
  }, [preview, deal?.id, workspaceId, getDealDetail]);
  const backendDeal = dealDetailData?.deal && dealDetailData.deal.id === deal?.id ? dealDetailData.deal : undefined;
  const backendAccount = backendDeal?.accounts?.edges?.[0]?.node;

  const currentDeal = { ...deal, ...overrides };

  const favorited = isFavorite(deal.id, "deal");
  const handleToggleFavorite = () => {
    toggleFavorite({ id: deal.id, type: "deal", name: deal.name, subtitle: `${deal.company || ""} · ${formatAmount(deal.amount || 0)}` });
  };

  /**
   * Placeholder activity feed (there is no activity API yet). Hoisted out of
   * the JSX so its ids and ISO dates read as the data they are, and so the
   * dates render through the shared formatter rather than as en-US literals.
   */
  const activityEntries = useMemo(() => [
    { id: "1", text: t("detailSheets.activityStageMoved", { from: "Discovery", to: "Proposal" }), date: formatDate("2026-02-25") },
    { id: "2", text: t("detailSheets.activityAmountUpdated", { amount: formatAmount(currentDeal.amount || 0) }), date: formatDate("2026-02-20") },
    { id: "3", text: t("detailSheets.activityCloseDateUpdated"), date: formatDate("2026-02-18") },
    { id: "4", text: t("detailSheets.activityDealCreated"), date: formatDate("2026-01-15") },
  ], [t, formatDate, formatAmount, currentDeal.amount]);

  if (subDrill) {
    return (
      <div>
        {subDrill.type === "email" && <EmailDrillDown email={subDrill.data} onBack={() => setSubDrill(null)} />}
        {subDrill.type === "file" && <FileDrillDown file={subDrill.data} onBack={() => setSubDrill(null)} />}
      </div>
    );
  }

  return (
    <div>
      <DrillDownHeader onBack={onBack} title="" subtitle="" />
      <div className="border-b border-border pb-4 mb-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
            {currentDeal.company ? <CompanyLogo name={currentDeal.company} domain={preview ? allCompanies.find(c => c.name === currentDeal.company)?.website : backendAccount?.domain ?? undefined} /> : <DollarSign className="h-5 w-5 text-muted-foreground" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <EditableDetailField
                value={currentDeal.name}
                onChange={v => setOverrides(prev => ({ ...prev, name: v }))}
                icon={<span />}
                className="text-lg font-semibold [&_span]:text-lg [&_span]:font-semibold [&_input]:text-lg [&_input]:font-semibold"
              />
              <button onClick={handleToggleFavorite} className="shrink-0 hover:scale-110 transition">
                <Star className={cn("h-3.5 w-3.5", favorited ? "fill-accent text-accent" : "text-muted-foreground")} />
              </button>
            </div>
            {currentDeal.company && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Building2 className="h-3 w-3 text-muted-foreground" />
                <EditableDetailField
                  value={currentDeal.company}
                  onChange={v => setOverrides(prev => ({ ...prev, company: v }))}
                  icon={<span />}
                  // Dummy company names must not be offered as suggestions on
                  // real data. This field only writes to local `overrides` (it
                  // never persists), so production simply offers none.
                  suggestions={preview ? companySuggestions : []}
                  className="text-sm text-accent"
                  onDrillDown={async () => {
                    if (!onNavigate) return;
                    if (preview) {
                      const comp = allCompanies.find(c => c.name === currentDeal.company);
                      if (comp) onNavigate({ type: "company", data: { id: comp.id, name: comp.name, industry: comp.industry, description: comp.description, location: comp.location, website: comp.website, employees: comp.employees, revenue: comp.revenue } });
                      return;
                    }
                    // Prefer the deal's real linked account; fall back to
                    // resolving the name over the backend.
                    if (backendAccount) {
                      onNavigate({ type: "company", data: { id: backendAccount.id, name: backendAccount.properties?.name || currentDeal.company || '', website: backendAccount.domain ?? undefined } });
                      return;
                    }
                    const resolved = currentDeal.company ? await resolveCompany(currentDeal.company) : null;
                    if (resolved) onNavigate({ type: "company", data: { id: resolved.id, name: resolved.name, website: resolved.website } });
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-muted/50 p-3 group cursor-pointer hover:ring-1 hover:ring-accent/30 transition">
            <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">{t("fields.deals.amount")} <Pencil className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition" /></p>
            <EditableDetailField
              value={formatAmount(currentDeal.amount || 0)}
              onChange={v => { const num = parseInt(v.replace(/[^0-9]/g, ""), 10); if (!isNaN(num)) setOverrides(prev => ({ ...prev, amount: num })); }}
              icon={<span />}
              className="text-lg font-semibold [&_span]:text-lg [&_span]:font-semibold"
            />
          </div>
          <div className="rounded-lg bg-muted/50 p-3 group cursor-pointer hover:ring-1 hover:ring-accent/30 transition">
            <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">{t("fields.deals.stage")} <Pencil className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition" /></p>
            <EditableDetailField
              value={currentDeal.stage || ""}
              onChange={v => setOverrides(prev => ({ ...prev, stage: v }))}
              icon={<span />}
              className="text-sm font-semibold"
            />
          </div>
        </div>
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-3 text-sm">
            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">{t("detailSheets.lastEmailLabel")} <button onClick={() => setSubDrill({ type: "email", data: dealEmails[0] })} className="text-foreground hover:text-accent hover:underline transition cursor-pointer">{dealEmails[0].date}</button></span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>{t("detailSheets.lastMeetingLabel")} <button onClick={() => setSubDrill({ type: "meeting", data: dealMeetings[0] })} className="text-foreground hover:text-accent hover:underline transition cursor-pointer">{dealMeetings[0].date}</button></span>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-6 mb-5">
          {/* `value` is the tab id — never translated; only the label is. */}
          <TabsTrigger value="summary" className="text-[11px] gap-1"><Sparkles className="h-3 w-3" />{t("detailSheets.tabSummary")}</TabsTrigger>
          <TabsTrigger value="people" className="text-[11px] gap-1"><Users className="h-3 w-3" />{t("people.title")}</TabsTrigger>
          <TabsTrigger value="meetings" className="text-[11px] gap-1"><Video className="h-3 w-3" />{t("detailSheets.tabMeetings")}</TabsTrigger>
          <TabsTrigger value="emails" className="text-[11px] gap-1"><Mail className="h-3 w-3" />{t("detailSheets.tabEmails")}</TabsTrigger>
          <TabsTrigger value="files" className="text-[11px] gap-1"><Paperclip className="h-3 w-3" />{t("detailSheets.tabFiles")}</TabsTrigger>
          <TabsTrigger value="activity" className="text-[11px] gap-1"><Activity className="h-3 w-3" />{t("detailSheets.tabActivity")}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3 text-accent" />
              <span>{t("detailSheets.aiGeneratedSummary")}</span>
            </div>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                <Trans
                  i18nKey="detailSheets.summaryStageLine"
                  values={{
                    name: currentDeal.name,
                    stage: currentDeal.stage || t("detailSheets.summaryStageFallback"),
                    amount: formatAmount(currentDeal.amount || 0),
                  }}
                  components={{
                    name: <span className="font-medium" />,
                    stage: <span className="font-medium text-accent" />,
                    amount: <span className="font-medium" />,
                  }}
                />
              </p>
              <p>
                {t("detailSheets.summaryActivityLine", {
                  meetingCount: dealMeetings.length,
                  emailCount: dealEmails.length,
                })}{" "}
                <Trans
                  i18nKey="detailSheets.summaryLastInteraction"
                  values={{ date: dealEmails[0]?.date, subject: dealEmails[0]?.subject }}
                  components={{ subject: <span className="italic" /> }}
                />
              </p>
              <p>
                <Trans
                  i18nKey="detailSheets.summaryMomentumLine"
                  components={{ positive: <span className="text-emerald-500 font-medium" /> }}
                />
              </p>
            </div>
            <p className="text-[10px] text-muted-foreground pt-1">{t("detailSheets.summaryLastUpdated")}</p>
          </div>
        </TabsContent>

        <TabsContent value="people">
          {(() => {
            // Production: the deal's real contacts (with real ids) from getDeal.
            // Preview: people at the matching dummy company.
            const dealPeople = preview
              ? (currentDeal.company ? allPeople.filter(p => p.company === currentDeal.company) : [])
                  .map(p => ({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone }))
              : (backendDeal?.contacts?.edges ?? []).flatMap(edge => {
                  const c = edge?.node;
                  if (!c) return [];
                  return [{
                    id: c.id,
                    name: getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail) || c.primaryEmail || t("detailSheets.unknownName"),
                    title: c.properties?.jobTitle || undefined,
                    company: backendAccount?.properties?.name || currentDeal.company,
                    email: c.primaryEmail || undefined,
                    // phone isn't on ContactBaseFields; the person sheet fetches it.
                  }];
                });
            return (
              <PeopleTable
                people={dealPeople}
                onRowClick={p => onNavigate ? onNavigate({ type: "person", data: p }) : setSubDrill({ type: "person", data: p })}
                showCompany
                emptyMessage={t("detailSheets.noPeopleOnDeal")}
              />
            );
          })()}
        </TabsContent>

        <TabsContent value="meetings" className="space-y-1">
          {dealMeetings.map(m => (
            <div key={m.id} onClick={() => setSubDrill({ type: "meeting", data: m })} className="flex items-center justify-between py-2.5 px-2 -mx-2 rounded-md hover:bg-muted/30 transition cursor-pointer group">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.title}</p>
                <p className="text-xs text-muted-foreground">{m.date} · {m.duration} · {m.attendees.join(", ")}</p>
              </div>
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0" />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="emails" className="space-y-1">
          {dealEmails.map(e => (
            <div key={e.id} onClick={() => setSubDrill({ type: "email", data: e })} className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-md hover:bg-muted/30 transition cursor-pointer">
              {e.direction === "inbound" ? <ArrowDownLeft className="h-3.5 w-3.5 text-accent shrink-0" /> : <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{e.subject}</p>
                <p className="text-xs text-muted-foreground">{e.date} · {e.from}</p>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="files" className="space-y-1">
          {dealFiles.map(f => (
            <div key={f.id} onClick={() => setSubDrill({ type: "file", data: f })} className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-md hover:bg-muted/30 transition cursor-pointer">
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.name}</p>
                <p className="text-xs text-muted-foreground">{f.size} · {f.date}</p>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="activity" className="space-y-2">
          {activityEntries.map(a => (
            <div key={a.id} className="flex items-start gap-3">
              <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground shrink-0" />
              <div>
                <p className="text-sm">{a.text}</p>
                <p className="text-xs text-muted-foreground">{a.date}</p>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Meeting Briefing drill-down (for upcoming meetings) ----
const briefingEmailSummaries: Record<string, string> = {
  "Sarah Chen": "12 emails exchanged since Jan 2026. Last thread discussed migration timeline and infrastructure requirements.",
  "Alex Kim": "8 emails exchanged since Feb 2026. Recent topics: API architecture, data security compliance.",
  "James Wilson": "15 emails exchanged since Dec 2025. Focused on contract terms, pricing tiers, and renewal timeline.",
  "Maria Garcia": "5 emails exchanged since Feb 2026. Initial outreach about marketing automation needs.",
  "Nina Rossi": "3 emails exchanged since Feb 2026. Discussed design collaboration and branding requirements.",
  "Rachel Adams": "9 emails exchanged since Jan 2026. Topics: operational workflows, team scaling, process optimization.",
  "Grace Liu": "4 emails exchanged since Feb 2026. Design system collaboration and brand guidelines review.",
};

const briefingMeetingNotes: Record<string, string> = {
  "Sarah Chen": "Last meeting (Feb 27): Reviewed Phase 1 migration plan. Sarah raised concerns about downtime windows.",
  "Alex Kim": "Last meeting (Feb 28): Deep dive into API integration. Alex suggested using streaming endpoints.",
  "James Wilson": "Last meeting (Feb 25): Contract review with legal. James requested a 10% volume discount.",
  "Maria Garcia": "Last meeting (Feb 24): Initial discovery call. Maria outlined current marketing stack pain points.",
  "Rachel Adams": "Last meeting (Feb 24): Discussed ops workflow improvements and quarterly planning timeline.",
};

function getMockBriefingAnswer(question: string, attendeeNames: string[]): string {
  const q = question.toLowerCase();
  if (/agenda|topic|discuss/i.test(q)) return `Likely agenda:\n\n• Review progress on current initiatives\n• Discuss open action items\n• Align on next steps and timeline\n\n${attendeeNames[0]} has been most active in recent threads.`;
  if (/risk|concern/i.test(q)) return `Key risks:\n\n• Timeline pressure flagged in recent emails\n• Budget alignment questions from ${attendeeNames[0]}\n• Resource availability may be a concern`;
  if (/prepare|ready/i.test(q)) return `To prepare:\n\n• Review the latest proposal shared on Feb 25\n• Check action items from the last meeting\n• Prepare talking points around timeline and pricing\n• Have competitive positioning ready`;
  // eslint-disable-next-line coffee-i18n/no-hardcoded-plural -- Mock briefing answer; only reachable from the Lovable preview.
  return `Here's what I found relevant to "${question}":\n\n• This meeting involves ${attendeeNames.length} attendees\n• Key topics from recent interactions include contract discussions and technical requirements\n• Recommend preparing talking points around mutual goals and open action items.`;
}

interface BriefingQAMessage { id: string; role: "user" | "ai"; text: string; }

// ---- Note detail drill-down ----
export function NoteDrillDown({ note, onBack }: { note: NoteItem; onBack: () => void }) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  const [updateNote] = useUpdateNoteMutation();
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Manual notes are editable; AI-generated notes stay read-only.
  const isEditable = !preview && note.type !== "ai" && !!workspaceId && !!note.id;

  // Auto-save edits with a 1s debounce (same pattern as MeetingIntelligenceSheet).
  const handleNoteChange = (updatedBlocks: PartialBlock[]) => {
    if (!isEditable) return;
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      updateNote({
        variables: {
          input: {
            id: note.id,
            workspaceId: String(workspaceId),
            blocks: JSON.stringify(updatedBlocks),
          },
        },
      }).catch((e) => {
        console.error('[NoteDrillDown] Failed to save note:', e);
        toast.error(t("detailSheets.noteSaveFailed"));
      });
    }, 1000);
  };

  useEffect(() => () => { if (saveTimeout.current) clearTimeout(saveTimeout.current); }, []);

  const formattedDate = (() => {
    if (!note.date) return '';
    try {
      const d = parseISO(note.date);
      if (isValid(d)) return formatDate(d);
    } catch { /* ignore */ }
    return note.date;
  })();

  const { data, loading } = useGetNoteQuery({
    variables: { id: note.id, workspaceId: String(workspaceId || '') },
    skip: preview || !workspaceId || !note.id,
    fetchPolicy: 'cache-and-network',
  });

  const blocks = useMemo<PartialBlock[] | null>(() => {
    const rawBlocks = data?.note?.blocks;
    if (rawBlocks) {
      try {
        const parsed = typeof rawBlocks === 'string' ? JSON.parse(rawBlocks) : rawBlocks;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed as PartialBlock[];
      } catch (e) {
        console.error('[NoteDrillDown] Failed to parse note blocks:', e);
      }
    }
    // Fallback: AI-generated notes store their content as AiNotesSection[] in aiNotesContent.
    const rawAi = data?.note?.aiNotesContent;
    if (rawAi) {
      try {
        const parsed = typeof rawAi === 'string' ? JSON.parse(rawAi) : rawAi;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return aiNotesToBlocks(parsed as AiNotesSection[]);
        }
      } catch (e) {
        console.error('[NoteDrillDown] Failed to parse aiNotesContent:', e);
      }
    }
    return null;
  }, [data?.note?.blocks, data?.note?.aiNotesContent]);

  return (
    <div className="h-full">
      <DrillDownHeader onBack={onBack} title={note.name} subtitle={`${note.type === "ai" ? t("detailSheets.aiNote") : t("detailSheets.manualNote")}${formattedDate ? ` · ${formattedDate}` : ''}`} />
      <div className="rounded-lg border border-border p-4">
        {preview ? (
          <div className="prose prose-sm max-w-none text-sm leading-relaxed">
            {note.content.split("\n").map((line, i) => {
              if (line.startsWith("## ")) return <h2 key={i} className="text-base font-semibold mt-3 mb-2">{line.replace("## ", "")}</h2>;
              if (line.startsWith("### ")) return <h3 key={i} className="text-sm font-semibold mt-2 mb-1">{line.replace("### ", "")}</h3>;
              if (line.startsWith("- **")) {
                const m = line.match(/^- \*\*(.+?)\*\*:?\s*(.*)/);
                if (m) return <p key={i} className="text-sm ml-4 my-0.5">• <strong>{m[1]}</strong>{m[2] ? ": " : ""}{m[2]}</p>;
              }
              if (line.startsWith("- ")) return <p key={i} className="text-sm ml-4 my-0.5">• {line.replace("- ", "")}</p>;
              if (line.trim() === "") return <div key={i} className="h-1.5" />;
              return <p key={i} className="text-sm my-0.5">{line}</p>;
            })}
          </div>
        ) : loading && !data ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm">{t("detailSheets.loadingNote")}</span>
          </div>
        ) : blocks ? (
          <NoteEditor key={note.id} initialContent={blocks} onChange={handleNoteChange} editable={isEditable} autoFocus={false} />
        ) : (
          <p className="text-muted-foreground text-xs py-4">{t("detailSheets.noteEmpty")}</p>
        )}
      </div>
    </div>
  );
}
